import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import {
  useCheckLogin,
  useChangePassword,
  useUpdateNickname,
} from '@/hooks/useAuth';

const ChangeAccount = () => {
  const navigate = useNavigate();
  const { data: userData, isLoading } = useCheckLogin();
  const changePasswordMutation = useChangePassword();
  const updateNicknameMutation = useUpdateNickname();

  const [nickname, setNickname] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentPasswordValid, setCurrentPasswordValid] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (userData?.nickname) {
      setNickname(userData.nickname);
    }
  }, [userData]);

  const handleCurrentPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setCurrentPassword(value);
    setCurrentPasswordValid(value.length >= 8);
  };

  const isOAuthUser = !!userData?.provider;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (isOAuthUser) {
      if (!nickname || nickname.trim() === '') {
        setError('Nickname cannot be empty');
        return;
      }

      try {
        await updateNicknameMutation.mutateAsync(nickname);
        navigate('/user/mypage');
      } catch (err: any) {
        setError(
          err?.response?.data?.message ||
            'Failed to update nickname. Please try again.'
        );
      }
      return;
    }

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Please fill in all password fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New password and confirm password do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }

    if (!userData?.email) {
      setError('User email not found');
      return;
    }

    try {
      await changePasswordMutation.mutateAsync({
        email: userData.email,
        oldPassword: currentPassword,
        newPassword: newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setCurrentPasswordValid(false);
      navigate('/user/mypage');
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          'Failed to update password. Please try again.'
      );
    }
  };

  if (isLoading) {
    return (
      <div className='min-h-screen bg-white flex items-center justify-center'>
        <p className='text-gray-600'>Loading...</p>
      </div>
    );
  }

  if (!userData) {
    navigate('/user/login');
    return null;
  }

  return (
    <div className='min-h-screen bg-white flex flex-col'>
      {/* Header */}
      <div className='flex h-full w-full items-center p-[20px] gap-[8px]'>
        <button
          onClick={() => navigate(-1)}
          className='flex items-center justify-center w-8 h-8'
        >
          <Icon icon='mdi:arrow-left' className='w-6 h-6 text-black' />
        </button>
        <p className='flex-1 text-center font-semibold text-black text-sm'>
          Change account
        </p>
        <div className='w-8' /> {/* Spacer for centering */}
      </div>

      {/* Form */}
      <div className='flex flex-col items-start flex-1 px-6 pb-8'>
        <form
          onSubmit={handleSubmit}
          className='w-full max-w-[400px] mx-auto flex flex-col gap-3'
        >
          {/* Email Field */}
          <div className='flex flex-col gap-2'>
            <label
              htmlFor='email'
              className='text-gray-600 text-sm font-medium'
            >
              Email
            </label>
            <input
              type='email'
              id='email'
              value={userData.email}
              disabled
              className='w-full h-12 px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-600 cursor-not-allowed'
            />
          </div>

          {/* Nickname Field */}
          <div className='flex flex-col gap-2'>
            <label
              htmlFor='nickname'
              className='text-gray-600 text-sm font-medium'
            >
              Nickname
            </label>
            <input
              type='text'
              id='nickname'
              value={nickname}
              onChange={e => setNickname(e.target.value)}
              disabled={!isOAuthUser}
              className={`w-full h-12 px-4 py-2 bg-white border border-gray-300 rounded-lg text-black placeholder:text-gray-400 focus:outline-none focus:border-[#fccb16] focus:ring-1 focus:ring-[#fccb16] ${
                !isOAuthUser
                  ? 'bg-gray-100 text-gray-600 cursor-not-allowed'
                  : ''
              }`}
              placeholder='Enter nickname'
            />
          </div>

          {/* Current Password Field */}
          <div className='flex flex-col gap-2'>
            <label
              htmlFor='currentPassword'
              className='text-gray-600 text-sm font-medium'
            >
              Current password
            </label>
            <div className='relative'>
              <input
                type='password'
                id='currentPassword'
                value={currentPassword}
                onChange={handleCurrentPasswordChange}
                placeholder='Enter current password'
                disabled={isOAuthUser}
                className={`w-full h-12 px-4 py-2 pr-10 border rounded-lg placeholder:text-gray-400 focus:outline-none ${
                  isOAuthUser
                    ? 'bg-gray-100 border-gray-300 text-gray-600 cursor-not-allowed'
                    : currentPasswordValid && currentPassword
                      ? 'bg-white border-[#fccb16] text-black focus:ring-1 focus:ring-[#fccb16]'
                      : 'bg-white border-gray-300 text-black focus:border-[#fccb16] focus:ring-1 focus:ring-[#fccb16]'
                }`}
              />
              {currentPasswordValid && currentPassword && !isOAuthUser && (
                <div className='absolute right-3 top-1/2 transform -translate-y-1/2'>
                  <Icon icon='mdi:check' className='w-5 h-5 text-[#fccb16]' />
                </div>
              )}
            </div>
          </div>

          {/* New Password Field */}
          <div className='flex flex-col gap-2'>
            <label
              htmlFor='newPassword'
              className='text-gray-600 text-sm font-medium'
            >
              New password
            </label>
            <input
              type='password'
              id='newPassword'
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder='Enter new password'
              disabled={isOAuthUser}
              className={`w-full h-12 px-4 py-2 border rounded-lg placeholder:text-gray-400 focus:outline-none ${
                isOAuthUser
                  ? 'bg-gray-100 border-gray-300 text-gray-600 cursor-not-allowed'
                  : 'bg-white border-gray-300 text-black focus:border-[#fccb16] focus:ring-1 focus:ring-[#fccb16]'
              }`}
            />
          </div>

          {/* Confirm Password Field */}
          <div className='flex flex-col gap-2'>
            <label
              htmlFor='confirmPassword'
              className='text-gray-600 text-sm font-medium'
            >
              Confirm password
            </label>
            <input
              type='password'
              id='confirmPassword'
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder='Confirm new password'
              disabled={isOAuthUser}
              className={`w-full h-12 px-4 py-2 border rounded-lg placeholder:text-gray-400 focus:outline-none ${
                isOAuthUser
                  ? 'bg-gray-100 border-gray-300 text-gray-600 cursor-not-allowed'
                  : 'bg-white border-gray-300 text-black focus:border-[#fccb16] focus:ring-1 focus:ring-[#fccb16]'
              }`}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className='text-red-600 text-sm bg-red-50 p-3 rounded-lg border border-red-200'>
              {error}
            </div>
          )}

          {/* Update Button */}
          <button
            type='submit'
            disabled={
              isOAuthUser
                ? updateNicknameMutation.isPending
                : changePasswordMutation.isPending
            }
            className='w-full h-12 bg-[#fccb16] text-black rounded-lg font-medium text-base cursor-pointer transition-all hover:bg-[#ffd84d] disabled:bg-gray-400 disabled:cursor-not-allowed'
          >
            {isOAuthUser
              ? updateNicknameMutation.isPending
                ? 'Updating...'
                : 'Update'
              : changePasswordMutation.isPending
                ? 'Updating...'
                : 'Update'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChangeAccount;
